import { saveLocalSettings } from '../lib/localSettings';
import LoggerWrapper from '../modules/logger-wrapper';
import { isEmpty, merge } from 'lodash';
const logger = new LoggerWrapper('STUDIO', 'studioReducer', LoggerWrapper.DEBUG);
logger.setAutoFunction(false);

// store intermediate devices update chunks
// connection id -> update id -> {pending, devices}
const participantDevicesUpdateChunksAccumulator = {};
// maintain orphaned devices update
// handling edge case of when devices update was completed before participant exists in participants array
// connection id -> devices
const orphanedDevicesUpdate = {};

const studioReducer = (draft, action) => {
	//console.log({ action }); // Local debug only.
	//draft is current state action is the object fired by dispatch
	switch (action.type) {
		case 'setDevices':
			draft.devices = action.payload;
			return;
		case 'addParticipant':
			const allConnections = draft.participants.map((participant) => participant.connectionId);
			if (allConnections.includes(action.payload.connectionId)) {
				return;
			}
			// add devices if waiting in orphanedDevicesUpdate
			let participant = action.payload;
			if (orphanedDevicesUpdate[participant.connectionId]) {
				participant = { ...participant, devices: orphanedDevicesUpdate[participant.connectionId] };
				delete orphanedDevicesUpdate[participant.connectionId];
			}
			draft.participants.push(participant);
			if (participant?.role === 'mediasharer') {
				draft.mediasharerConnected = true;
			}
			// Update current participant liveCallID based on existing.
			if (action.payload.liveCallID && !draft.currentParticipant.liveCallID) {
				draft.currentParticipant.liveCallID = action.payload.liveCallID;
				draft.studioGlobalTracking.studio.liveCallId = action.payload.liveCallID;
			}
			return;
		case 'updateParticipant':
			logger.debug(`Update participant`, { participant: action.payload });
			const userIndex = draft.participants.findIndex(
				(participant) => participant.connectionId === action.payload.connectionId
			);
			if (userIndex !== -1) {
				draft.participants.splice(userIndex, 1);
				draft.participants.splice(userIndex, 0, action.payload); // Preserve order!!
			}
			// Update current participant liveCallID based on existing.
			if (action.payload.liveCallID && !draft.currentParticipant.liveCallID) {
				draft.currentParticipant.liveCallID = action.payload.liveCallID;
				draft.studioGlobalTracking.studio.liveCallId = action.payload.liveCallID;
			}
			return;
		case 'updateParticipantDevices':
			const { connectionId, updateId, devicesChunk, numberOfChunks } = action.payload;
			try {
				// logger.debug(`Update participant devices`, { connectionId, updateId, numberOfChunks });
				if (!participantDevicesUpdateChunksAccumulator[connectionId]) {
					participantDevicesUpdateChunksAccumulator[connectionId] = {};
				}
				if (!participantDevicesUpdateChunksAccumulator[connectionId][updateId]) {
					participantDevicesUpdateChunksAccumulator[connectionId][updateId] = {
						pending: numberOfChunks - 1,
						devices: devicesChunk,
					};
				} else {
					--participantDevicesUpdateChunksAccumulator[connectionId][updateId].pending;
					participantDevicesUpdateChunksAccumulator[connectionId][updateId].devices += devicesChunk;
				}
				const current = participantDevicesUpdateChunksAccumulator[connectionId][updateId];
				if (current.pending > 0) {
					return;
				}
				const devices = JSON.parse(current.devices);
				delete participantDevicesUpdateChunksAccumulator[connectionId][updateId];
				if (isEmpty(participantDevicesUpdateChunksAccumulator[connectionId])) {
					delete participantDevicesUpdateChunksAccumulator[connectionId];
				}
				// logger.debug(`Update participant devices - commit`, { devices, participantDevicesUpdateChunksAccumulator });
				const participantIndex = draft.participants.findIndex(
					(participant) => participant.connectionId === connectionId
				);
				if (participantIndex !== -1) {
					const participant = draft.participants[participantIndex];
					draft.participants.splice(participantIndex, 1);
					draft.participants.splice(participantIndex, 0, { ...participant, devices }); // Preserve order!!
					delete orphanedDevicesUpdate[connectionId]; // delete orphaned devices for participant, if exists
				} else {
					// logger.debug(
					// 	`Update participant devices - no participant found for devices, adding to orphanedDevicesUpdate`,
					// 	{
					// 		devices,
					// 		connectionId,
					// 	}
					// );
					orphanedDevicesUpdate[connectionId] = devices;
				}
			} catch (e) {
				try {
					logger.debug(`updateParticipantDevices`, {
						payload: action.payload,
						accumulator: participantDevicesUpdateChunksAccumulator[connectionId][updateId],
						error: e,
					});
				} catch (e) {
					logger.debug(`updateParticipantDevices - error logging exception`, {});
				}
			}
			return;
		case 'updateCurrentParticipant':
			draft.currentParticipant = {
				...draft.currentParticipant,
				...action.payload,
			};

			if (draft.currentParticipant?.callingInStatus === 'accepted') {
				draft.liveCallInHasBeenAccepted = true;
			}
			saveLocalSettings(action.payload);
			return;
		case 'removeParticipant':
			const index = draft.participants.findIndex((participant) => participant.connectionId === action.payload);
			if (index !== -1) {
				if (draft.mediasharerConnected && draft.participants[index]?.role === 'mediasharer') {
					draft.mediasharerConnected = false;
				}
				draft.participants.splice(index, 1);
			}
			return;
		case 'updateDevices':
			draft.devices = action.payload;
			return;
		case 'newDevices':
			// action.payload.map((device) => {
			//   draft.toasts.push({ type: "device", payload: device });
			// });
			return;
		case 'setActiveDevices':
			const devices = action.payload;
			draft.currentParticipant.activeDevices = devices;
			if (devices.audio) {
				draft.studioGlobalTracking.devices.activeAudioInputLabel = devices.audio.label;
				draft.studioGlobalTracking.devices.echoCancellation = devices.audio.echoCancellation;
				draft.studioGlobalTracking.devices.noiseSuppression = devices.audio.noiseSuppression;
				draft.studioGlobalTracking.devices.audioLatency = devices.audio.latency;
			}

			if (devices.video) {
				draft.studioGlobalTracking.devices.activeVideoInputLabel = devices.video.label;
			}
			return;
		case 'updateGlobalTracking':
			//console.log('[tracking] update global tracking', action.payload);
			draft.studioGlobalTracking = merge(draft.studioGlobalTracking, action.payload);
			return;
		// case 'updateActiveDevices':
		// 	draft.currentParticipant.activeDevices = { ...draft.currentParticipant.activeDevices, ...action.payload };
		// 	return;
		case 'setWaitingRoomParticipants':
			draft.waitingRoomParticipants = action.payload;
			return;
		case 'removeWaitingRoomParticipant':
			const memberIndex = draft.waitingRoomParticipants.findIndex((member) => member === action.payload);
			draft.waitingRoomParticipants.splice(memberIndex, 1);
			return;
		case 'removeNewDevices':
			action.payload.map((device) => {
				const index = draft.toasts.findIndex((toast) => {
					return toast.payload?.deviceId === device?.deviceId;
				});
				if (index !== -1) draft.toasts.splice(index, 1);
			});
			return;
		case 'setScreenShared':
			draft.screenShared = action.payload;
			if (draft.screenShared) {
				draft.isVideoShared = true;
			}
			return;
		case 'setVideoShared':
			draft.isVideoShared = action.payload;
			return;
		case 'setMessages':
			draft.messages = action.payload;
			return;
		case 'appendMessage':
			draft.messages = [...draft.messages, action.payload];
			return;
		case 'setMessagesFetched':
			draft.messagesFetched = action.payload;
			return;
		case 'shouldJoinGreenRoom':
			draft.shouldJoinGreenRoom = action.payload;
			return;
		case 'setRecordingStatus':
			logger.debug(`setRecordingStatus`, { payload: action.payload });
			console.log('setting recording status', action.payload);
			draft.recording.status = action.payload?.status;
			draft.recording.sessionId = action.payload?.sessionId || draft.recording?.sessionId;
			draft.recording.countDownEnd = action.payload?.countDownEnd || draft.recording?.countDownEnd;
			draft.currentParticipant.recording = action.payload.status === 'recording';
			return;
		case 'setRecordingLoading':
			console.log(
				'setting loading to',
				typeof action.payload !== 'boolean' ? draft.recording?.loading : action.payload
			);
			draft.recording.loading = typeof action.payload !== 'boolean' ? draft.recording?.loading : action.payload;
			return;
		case 'sessionStartRecordingCountdown':
			draft.sessionRecording.status = 'starting';
			return;
		case 'sessionStartRecording':
			draft.sessionRecording.status = 'recording';
			return;
		case 'sessionStopRecording':
			draft.sessionRecording.status = 'ready';
			return;
		case 'sessionUpdateRecording':
			draft.sessionRecording.status = action.payload.status;
			return;
		case 'setShowStatus':
			draft.showData.status = action.payload;
			return;
		case 'setStudioSettings':
			const payload = { ...action.payload };
			if (payload?.showDate && typeof payload.showDate === 'string') {
				payload.showDate = JSON.parse(payload.showDate);
			}
			draft.studioSettings = payload;
		case 'setPublishCamera':
			draft.publishCamera = action.payload;
			return;
		case 'setPublishScreen':
			draft.publishScreen = action.payload;
			return;
		case 'setPublicRole':
			draft.publicRole = action.payload;
			return;
		case 'toggleVideo':
			draft.currentParticipant = {
				...draft.currentParticipant,
				hasVideo: action.payload,
			};
			return;
		case 'toggleAudio':
			draft.currentParticipant = {
				...draft.currentParticipant,
				hasAudio: action.payload,
			};
			return;
		case 'toggleAudioAndVideo':
			draft.hasAudio = action.payload;
			draft.hasVideo = action.payload;
			return;
		case 'setInviteDialogOpen':
			draft.inviteDialogOpen = action.payload;
			return;
		case 'setActiveAudioOutput':
			draft.activeAudioOutput = action.payload;
			return;
		case 'setAudioBlocked':
			draft.audioBlocked = action.payload;
			return;
		case 'setRecordingName':
			draft.recordingName = action.payload;
			return;
		case 'setIsSessionConnected':
			draft.isSessionConnected = action.payload;
			return;
		case 'sessionReconnecting':
			draft.isSessionReconnecting = true;
			return;
		case 'sessionReconnected':
			draft.isSessionReconnecting = false;
			return;
		case 'setLiveCallID':
			draft.currentParticipant.liveCallID = action.payload;
			draft.studioGlobalTracking.studio.liveCallId = action.payload;
		case 'setSettingsDialogOpen':
			draft.settingsDialogOpen = action.payload;
			return;
		case 'updateRTMPSettings':
			draft.showData.rtmp = action.payload;
			return;
		case 'setParticipantRepublishing':
			draft.currentParticipant = {
				...draft.currentParticipant,
				republishing: action.payload,
			};
			return;
		case 'terminateSession':
			draft.sessionTerminated = true;
			draft.currentParticipant.isActive = false;
			draft.sessionRecording = 'ready';
			if (draft.recording.status !== 'idle') {
				draft.recording.status = 'stopped';
			}
			return;
		case 'endCallIn':
			if (draft.recording.status !== 'idle') {
				draft.recording.status = 'stopped';
			}
			draft.currentParticipant.callingInStatus = '';
			draft.currentParticipant.publishEnabled = false;
			return;
		case 'addToast':
			if (action.payload.takeLatest) {
				draft.toasts = draft.toasts.filter((toast) => !(toast.type === action.payload.type));
				draft.toasts.push(action.payload);
				return;
			}
			if (draft.toasts.find((toast) => toast.type === action.payload.type)) {
				return;
			}
			draft.toasts.push(action.payload);
			return;
		case 'removeToast':
			const removeIdx = draft.toasts.findIndex((toast) => toast.uuid === action.payload);
			if (removeIdx === -1) return;
			draft.toasts.splice(removeIdx, 1);
			return;
		case 'setDirHandle':
			draft.dirHandle = action.payload;
			return;
		case 'setHasFilePermissions':
			draft.hasFilePermissions = action.payload;
			return;
		case 'dismissAlert':
			let currentDismissals = draft.dismissedAlerts[action.payload.connectionId];
			if (!currentDismissals) {
				currentDismissals = [];
			}
			draft.dismissedAlerts[action.payload.connectionId] = [...currentDismissals, action.payload.alert];
			return;
		case 'setConstraints':
			draft.constraints = action.payload;
			return;
		case 'setError':
			draft.error = action.payload;
			if (draft.error?.type === 'trackStartError') {
				if (draft.error?.constraints?.video) {
					draft.hasVideo = false;
				}
				if (draft.error?.constraints?.audio) {
					draft.hasAudio = false;
				}
			}
			return;
		case 'removeError':
			draft.error = '';
			return;
		case 'setRepublishing':
			draft.republishing = action.payload;
			if (!action.payload) {
				draft.currentParticipant = { ...draft.currentParticipant, republishing: false };
			}
			return;
		case 'setStreamGenerating':
			draft.streamGenerating = action.payload;
			return;
		case 'setIsStreamPendingPublish':
			draft.isStreamPendingPublish = action.payload;
			return;
		case 'setHeadphones':
			if (!action.payload) return;
			draft.headphones = action.payload;
			draft.currentParticipant = {
				...draft.currentParticipant,
				alerts:
					draft.headphones === 'yes'
						? draft.currentParticipant.alerts.filter((alert) => alert !== 'noHeadphones')
						: [...draft.currentParticipant.alerts, 'noHeadphones'],
				echoCancellation: action.payload === 'no',
			};
			saveLocalSettings({ echoCancellation: action.payload === 'no' });
			return;
		case 'browserCheckResult':
			draft.browserCheck = { ...draft.browserCheck, ...action.payload };
			return;
		case 'browserCheckIgnore':
			draft.browserCheck.ignored = true;
			return;
		case 'addPendingRequest':
			if (action.payload.requestName === 'changeDevice') {
				// check for existing request
				const index = draft.pendingRequests.findIndex(
					(request) =>
						request.requestName === 'changeDevice' &&
						(request.recipientId === 'currentParticipantId' || request.recipientId === action.payload.recipientId)
				);
				if (index !== -1) {
					draft.pendingRequests[index] = action.payload;
					return;
				}
			}
			draft.pendingRequests.push(action.payload);
			return;
		case 'removePendingRequest':
			draft.pendingRequests = draft.pendingRequests.filter((request) => request.id !== action.payload.requestId);
			return;
		case 'removePendingRequestsByName':
			draft.pendingRequests = draft.pendingRequests.filter(
				(request) => request.requestName !== action.payload.requestName
			);
			return;
		case 'removePendingRequestByRecipientId':
			draft.pendingRequests = draft.pendingRequests.filter(
				(request) => request.recipientId !== action.payload.data.recipientId
			);
			return;
		case 'setRecordedParticipants':
			draft.recordedParticipants = action.payload;
			return;
		case 'toggleVP9':
			draft.shouldRecordWithVP9 = action.payload;
			console.log('setting should recording in vp9', action.payload);
			return;
		case 'setCountDownEnd':
			draft.countDownEnd = action.payload;
			return;
		case 'updateCurrentResolution':
			draft.currentParticipant.resolutionBeforeAdapter = action.payload;
			return;
		default:
			console.error(`unknown action type: ${action.type} doesn't exist in studio reducer`);
			return;
	}
};

export default studioReducer;
